import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Save, Plus, Trash2, Eye, Loader2 } from "lucide-react";

const field = "w-full border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue";

export default function InsightEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get(`/admin/insights/${id}`).then(({ data }) => setA(data)).catch(() => { toast.error("Not found"); navigate("/admin/insights"); });
  }, [id, navigate]);

  const upd = (k, v) => setA((p) => ({ ...p, [k]: v }));
  const updBlock = (i, v) => setA((p) => ({ ...p, body: p.body.map((b, j) => (j === i ? { ...b, text: v } : b)) }));
  const addBlock = (type) => setA((p) => ({ ...p, body: [...(p.body || []), { type, text: "" }] }));
  const removeBlock = (i) => setA((p) => ({ ...p, body: p.body.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/insights/${id}`, a);
      setA(data); toast.success("Saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const publish = async () => {
    await save();
    try { await api.post(`/admin/insights/${id}/publish`); toast.success("Published"); setA((p) => ({ ...p, status: "published" })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!a) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-restrained-blue" /></div>;

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/admin/insights")} className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-restrained-blue mb-6"><ArrowLeft size={15} /> Insights</button>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-serif text-3xl text-deep-navy">Edit article</h1>
          <span className={`text-xs px-2 py-0.5 ${a.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPreview((v) => !v)} data-testid="insight-preview-toggle" className="inline-flex items-center gap-2 border border-[#E5E5E5] px-4 py-2.5 text-sm hover:bg-off-white"><Eye size={15} /> {preview ? "Edit" : "Preview"}</button>
          <button onClick={save} disabled={saving} data-testid="insight-save" className="inline-flex items-center gap-2 border border-deep-navy text-deep-navy px-4 py-2.5 text-sm hover:bg-deep-navy hover:text-white transition-colors">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save draft</button>
          <button onClick={publish} data-testid="insight-publish-btn" className="inline-flex items-center gap-2 bg-deep-navy text-white px-4 py-2.5 text-sm hover:bg-restrained-blue transition-colors">Publish</button>
        </div>
      </div>

      {preview ? (
        <div className="bg-white border border-[#E5E5E5] p-8 md:p-12">
          <p className="overline text-restrained-blue">{a.category}</p>
          <h1 className="font-serif text-4xl text-deep-navy mt-3">{a.title}</h1>
          <p className="font-serif text-2xl text-deep-navy/80 mt-6 mb-8 pb-8 border-b border-[#E5E5E5]">{a.excerpt}</p>
          <div className="editorial-body">
            {(a.body || []).map((b, i) => b.type === "heading"
              ? <h2 key={i} className="font-serif text-2xl text-deep-navy mt-8 mb-4">{b.text}</h2>
              : <p key={i}>{b.text}</p>)}
          </div>
        </div>
      ) : (
        <div className="space-y-5 bg-white border border-[#E5E5E5] p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="overline text-charcoal/60 block mb-2">Title *</label><input className={field} value={a.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="editor-title" /></div>
            <div><label className="overline text-charcoal/60 block mb-2">Category *</label><input className={field} value={a.category || ""} onChange={(e) => upd("category", e.target.value)} data-testid="editor-category" placeholder="e.g. Perspective" /></div>
            <div><label className="overline text-charcoal/60 block mb-2">Author</label><input className={field} value={a.author || ""} onChange={(e) => upd("author", e.target.value)} /></div>
            <div><label className="overline text-charcoal/60 block mb-2">Read time</label><input className={field} value={a.read_time || ""} onChange={(e) => upd("read_time", e.target.value)} placeholder="5 min read" /></div>
          </div>
          <div><label className="overline text-charcoal/60 block mb-2">Slug</label><input className={field} value={a.slug || ""} onChange={(e) => upd("slug", e.target.value)} /></div>
          <div><label className="overline text-charcoal/60 block mb-2">Cover image URL</label><input className={field} value={a.cover_image || ""} onChange={(e) => upd("cover_image", e.target.value)} /></div>
          <div><label className="overline text-charcoal/60 block mb-2">Excerpt *</label><textarea rows={2} className={`${field} resize-none`} value={a.excerpt || ""} onChange={(e) => upd("excerpt", e.target.value)} data-testid="editor-excerpt" /></div>

          <div className="border-t border-[#E5E5E5] pt-5">
            <p className="overline text-charcoal/60 mb-2">SEO</p>
            <div className="space-y-3">
              <input className={field} value={a.seo_title || ""} onChange={(e) => upd("seo_title", e.target.value)} placeholder="SEO title" />
              <textarea rows={2} className={`${field} resize-none`} value={a.seo_description || ""} onChange={(e) => upd("seo_description", e.target.value)} placeholder="SEO meta description" />
            </div>
          </div>

          <div className="border-t border-[#E5E5E5] pt-5">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-charcoal/60">Body</p>
              <div className="flex gap-2">
                <button onClick={() => addBlock("heading")} className="inline-flex items-center gap-1 text-xs border border-[#E5E5E5] px-3 py-1.5 hover:bg-off-white"><Plus size={13} /> Heading</button>
                <button onClick={() => addBlock("paragraph")} data-testid="editor-add-paragraph" className="inline-flex items-center gap-1 text-xs border border-[#E5E5E5] px-3 py-1.5 hover:bg-off-white"><Plus size={13} /> Paragraph</button>
              </div>
            </div>
            <div className="space-y-3">
              {(a.body || []).map((b, i) => (
                <div key={i} className="flex gap-2">
                  <span className="overline text-charcoal/30 pt-3 w-16 shrink-0">{b.type}</span>
                  <textarea rows={b.type === "heading" ? 1 : 3} className={`${field} resize-none ${b.type === "heading" ? "font-serif text-lg" : ""}`} value={b.text} onChange={(e) => updBlock(i, e.target.value)} placeholder={b.type === "heading" ? "Section heading" : "Paragraph text"} />
                  <button onClick={() => removeBlock(i)} className="text-charcoal/30 hover:text-destructive pt-2"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-charcoal/40 border-t border-[#E5E5E5] pt-4">Publishing is blocked while required fields are empty or unresolved placeholders (TODO, TBD, [[…]]) remain in the content.</p>
        </div>
      )}
    </div>
  );
}
